import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Form, Formik } from "formik";
import { initialValues, initialValuesEdit } from "../schemas/initialValues";
import { validationsCreate, validationsEdit } from "../schemas/validations";
import form from "../schemas/forms";
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  cepApplyMask,
  cnpjApplyMask,
  cpfApplyMask,
  numbersOnly,
  phoneApplyMask,
} from "helpers/utils";

import { getCep, getType } from "services/uteis";
import FormField from "layouts/pages/users/new-user/components/FormField";
import { ICreateOrganizador, IListOrganizador } from "./interfaces";

import { editOrganizador, saveOrganizador } from "services/organizador";
import { setMessage, useMaterialUIController } from "context";
import MDAvatar from "components/MDAvatar";

import { uploadAvatarUser } from "services/users";
import { ITipoSystem } from "interfaces/typesystem";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  ORGANIZADORES_ACCENT,
  saveButtonSx,
  sectionTitleSx,
} from "../organizadores.theme";

interface Props {
  open: boolean;
  data?: IListOrganizador | null;
  onClose: () => void;
  updateListOrganizador: (
    type: "edit" | "create",
    organizador: IListOrganizador,
  ) => void;
}

function SectionHeader({ icon, title }: { icon: JSX.Element; title: string }) {
  return (
    <Box sx={sectionTitleSx}>
      <Box sx={{ color: ORGANIZADORES_ACCENT, display: "flex" }}>{icon}</Box>
      <Typography variant="button" fontWeight="bold">
        {title}
      </Typography>
    </Box>
  );
}

export default function ModalOrganizador({
  open,
  onClose,
  data,
  updateListOrganizador,
}: Props) {
  const [isCepFound, setIsCepFound] = useState<boolean>(false);
  const [, dispatch] = useMaterialUIController();
  const [avatar, setAvatar] = useState<globalThis.File | null>(null);
  const [types, setTypes] = useState<ITipoSystem>();
  const { formId, formField } = form;
  const {
    cnpj,
    cpf,
    nomeFantasia,
    razaoSocial,
    urlFacebook,
    urlInstagram,
    urlTwitter,
    urlSite,
    mediaSocialPageEvento,
    celPhone1,
    celPhone2,
    telefone,
    email,
    cep,
    logradouro,
    numero,
    cidade,
    bairro,
    complemento,
    uf,
    password,
    confirmPassword,
    tipoPessoa,
  } = formField;

  useEffect(() => {
    void getType().then((resp) => setTypes(resp));
  }, []);

  const dataInitialValuesEdit = data && initialValuesEdit(data);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const isEdit = Boolean(data);

  const handlerCep = async (
    _cep: string,
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void,
  ) => {
    const onlyCep = numbersOnly(_cep);
    if (onlyCep.length === 8) {
      await getCep(onlyCep).then((resp) => {
        if (resp.erro) {
          setIsCepFound(false);
          setFieldValue(cep.name, "");
          setFieldValue(logradouro.name, "");
          setFieldValue(cidade.name, "");
          setFieldValue(uf.name, "");
          setFieldValue(bairro.name, "");
          alert(`Cep '${_cep}' não localizado`);
        } else {
          setIsCepFound(true);
          setFieldValue(cep.name, cepApplyMask(onlyCep));
          setFieldValue(logradouro.name, resp.logradouro);
          setFieldValue(cidade.name, resp.localidade);
          setFieldValue(uf.name, resp.uf);
          setFieldValue(bairro.name, resp.bairro);
          numberInputRef.current?.focus();
        }
      });
    } else {
      setIsCepFound(false);
      setFieldValue(cep.name, "");
      setFieldValue(logradouro.name, "");
      setFieldValue(cidade.name, "");
      setFieldValue(uf.name, "");
      setFieldValue(bairro.name, "");
    }
  };

  const tipoPessoaId = (value: string) => {
    return types!.tipoPessoa.find((item) => item.tipo === value)!.id;
  };

  const handlerUploadAvatarUser = async (userId: string) => {
    const fileAvatar = new FormData();
    fileAvatar.append("avatar", avatar!);
    await uploadAvatarUser(userId, fileAvatar);
  };

  const submit = async (params: Record<string, unknown>) => {
    if (!types) return;
    const formValues = params as unknown as ICreateOrganizador;

    if (data) {
      const dataEdit: ICreateOrganizador = {
        ...formValues,
        id: data.id,
        tipoPessoaId: tipoPessoaId(params.tipoPessoa as string),
      };
      await editOrganizador(dataEdit).then(async () => {
        if (avatar) await handlerUploadAvatarUser(data.user.id);
        updateListOrganizador("edit", data);
        setMessage(dispatch, {
          open: true,
          text: "Organizador atualizado com sucesso",
        });
        handlerClose();
      });
    } else {
      const dataCreate: ICreateOrganizador = {
        ...formValues,
        tipoPessoaId: tipoPessoaId(params.tipoPessoa as string),
      };
      await saveOrganizador(dataCreate).then(async (resp) => {
        if (resp.status === 201) {
          updateListOrganizador("create", resp.data);
          if (avatar) await handlerUploadAvatarUser(resp.data.user.id);
          setMessage(dispatch, { open: true, text: "Organizador criado com sucesso" });
          handlerClose();
        } else {
          setMessage(dispatch, {
            open: true,
            text: resp.data.message,
            severity: "error",
          });
        }
      });
    }
  };

  const handlerClose = () => {
    setAvatar(null);
    onClose();
  };

  function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const { files } = event.currentTarget;
    if (!files) return;
    setAvatar(files[0]);
  }

  const previewURL = useMemo(() => {
    if (!avatar) return null;
    return URL.createObjectURL(avatar);
  }, [avatar]);

  return (
    <Dialog
      open={open}
      onClose={handlerClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <GroupsOutlinedIcon sx={{ fontSize: 40, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                {isEdit ? "Editar organizador" : "Novo organizador"}
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {isEdit ? data?.nomeFantasia : "Cadastro de organizador"}
              </Typography>
              {isEdit ? (
                <Box mt={0.75}>
                  <Chip
                    size="small"
                    label={`${data!._count?.eventos ?? 0} evento(s)`}
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          </Stack>
          <IconButton onClick={handlerClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0, overflowY: "auto" }}>
        <Formik
          key={data?.id ?? "new"}
          initialValues={data ? dataInitialValuesEdit : initialValues}
          validationSchema={data ? validationsEdit : validationsCreate}
          onSubmit={(values) => {
            void submit(values);
          }}
        >
          {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
            <Form id={formId}>
              <MDBox p={3}>
                {/* Avatar */}
                <MDBox
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "grey.200",
                    bgcolor: "grey.50",
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <IconButton component="label" sx={{ p: 0 }}>
                        <MDAvatar
                          bgColor="secondary"
                          src={avatar ? previewURL ?? "" : data?.user?.avatar || ""}
                          alt="profile-image"
                          size="xl"
                          shadow="sm"
                        />
                        <input
                          name="fileAvatar"
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleFileSelected}
                        />
                      </IconButton>
                    </Grid>
                    <Grid item xs>
                      <MDTypography variant="h6" fontWeight="bold">
                        {values.nomeFantasia.toLocaleUpperCase() || "Avatar do organizador"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        {values.razaoSocial || "Clique na imagem para enviar um avatar"}
                      </MDTypography>
                    </Grid>
                  </Grid>
                </MDBox>

                <SectionHeader icon={<BusinessOutlinedIcon fontSize="small" />} title="Dados do organizador" />
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6}>
                    {types && (
                      <Autocomplete
                        defaultValue={data ? values.tipoPessoa : null}
                        options={types.tipoPessoa.map((item) => item.tipo)}
                        onChange={handleChange}
                        onBlur={handleChange}
                        renderInput={(params) => (
                          <FormField
                            {...params}
                            value={values.tipoPessoa}
                            name={tipoPessoa.name}
                            label={tipoPessoa.label}
                            InputLabelProps={{ shrink: true }}
                            error={errors.tipoPessoa && touched.tipoPessoa}
                            sx={fieldSx}
                          />
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {values.tipoPessoa === "Pessoa Física" ? (
                      <FormField
                        type={cpf.type}
                        label={cpf.label}
                        name={cpf.name}
                        value={
                          values.cpf.length === 11
                            ? cpfApplyMask(values.cpf)
                            : numbersOnly(values.cpf)
                        }
                        onChange={handleChange}
                        error={errors.cpf && touched.cpf}
                        success={values.cpf.length > 0 && !errors.cpf}
                        sx={fieldSx}
                      />
                    ) : (
                      <FormField
                        type={cnpj.type}
                        label={cnpj.label}
                        name={cnpj.name}
                        variant="outlined"
                        value={
                          values.cnpj.length === 14
                            ? cnpjApplyMask(values.cnpj)
                            : numbersOnly(values.cnpj)
                        }
                        onChange={handleChange}
                        error={errors.cnpj && touched.cnpj}
                        success={values.cnpj.length > 0 && !errors.cnpj}
                        sx={fieldSx}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={nomeFantasia.type}
                      label={nomeFantasia.label}
                      name={nomeFantasia.name}
                      value={values.nomeFantasia}
                      onChange={handleChange}
                      error={errors.nomeFantasia && touched.nomeFantasia}
                      success={values.nomeFantasia.length > 0 && !errors.nomeFantasia}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={razaoSocial.type}
                      label={razaoSocial.label}
                      name={razaoSocial.name}
                      value={values.razaoSocial}
                      onChange={handleChange}
                      error={errors.razaoSocial && touched.razaoSocial}
                      success={values.razaoSocial.length > 0 && !errors.razaoSocial}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>

                <SectionHeader icon={<ShareOutlinedIcon fontSize="small" />} title="Redes sociais e site" />
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={urlFacebook.type}
                      label={urlFacebook.label}
                      name={urlFacebook.name}
                      value={values.urlFacebook}
                      placeholder="https://"
                      onChange={handleChange}
                      error={errors.urlFacebook && touched.urlFacebook}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={urlInstagram.type}
                      label={urlInstagram.label}
                      name={urlInstagram.name}
                      value={values.urlInstagram}
                      placeholder="https://"
                      onChange={handleChange}
                      error={errors.urlInstagram && touched.urlInstagram}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={urlTwitter.type}
                      label={urlTwitter.label}
                      name={urlTwitter.name}
                      value={values.urlTwitter}
                      placeholder="https://"
                      onChange={handleChange}
                      error={errors.urlTwitter && touched.urlTwitter}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={urlSite.type}
                      label={urlSite.label}
                      name={urlSite.name}
                      value={values.urlSite}
                      placeholder="https://"
                      onChange={handleChange}
                      error={errors.urlSite && touched.urlSite}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
                <MDBox display="flex" alignItems="center" mb={3}>
                  <MDTypography variant="caption" color="text.secondary">
                    Exibir redes sociais no site do evento
                  </MDTypography>
                  <Switch
                    sx={{ ml: 1 }}
                    checked={values.mediaSocialPageEvento !== "false"}
                    onChange={() =>
                      setFieldValue(
                        mediaSocialPageEvento.name,
                        values.mediaSocialPageEvento === "false" ? "true" : "false",
                      )
                    }
                  />
                </MDBox>

                <SectionHeader icon={<ContactPhoneOutlinedIcon fontSize="small" />} title="Contatos" />
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={celPhone1.type}
                      label={celPhone1.label}
                      name={celPhone1.name}
                      value={
                        values.celPhone1.length === 11
                          ? phoneApplyMask(values.celPhone1)
                          : numbersOnly(values.celPhone1)
                      }
                      placeholder="(00) 00000-0000"
                      onChange={handleChange}
                      error={errors.celPhone1 && touched.celPhone1}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={celPhone2.type}
                      label={celPhone2.label}
                      name={celPhone2.name}
                      value={
                        values.celPhone2.length === 11
                          ? phoneApplyMask(values.celPhone2)
                          : numbersOnly(values.celPhone2)
                      }
                      placeholder="(00) 00000-0000"
                      onChange={handleChange}
                      error={errors.celPhone2 && touched.celPhone2}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={telefone.type}
                      label={telefone.label}
                      name={telefone.name}
                      value={
                        values.telefone.length === 11
                          ? phoneApplyMask(values.telefone)
                          : numbersOnly(values.telefone)
                      }
                      placeholder="(00) 0000-0000"
                      onChange={handleChange}
                      error={errors.telefone && touched.telefone}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type={email.type}
                      label={email.label}
                      name={email.name}
                      value={values.email}
                      placeholder="email@..."
                      onChange={handleChange}
                      error={errors.email && touched.email}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>

                <SectionHeader icon={<LocationOnOutlinedIcon fontSize="small" />} title="Endereço" />
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={3}>
                    <FormField
                      type={cep.type}
                      label={cep.label}
                      name={cep.name}
                      value={values.cep}
                      placeholder="00.000-000"
                      onChange={handleChange}
                      onBlur={() => {
                        void handlerCep(values.cep, setFieldValue);
                      }}
                      error={errors.cep && touched.cep}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <FormField
                      disabled={isCepFound}
                      type={logradouro.type}
                      label={logradouro.label}
                      name={logradouro.name}
                      value={values.logradouro}
                      onChange={handleChange}
                      error={errors.logradouro && touched.logradouro}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormField
                      type={numero.type}
                      label={numero.label}
                      name={numero.name}
                      value={values.numero}
                      onChange={handleChange}
                      error={errors.numero && touched.numero}
                      sx={fieldSx}
                      inputProps={{ ref: numberInputRef }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormField
                      disabled={isCepFound}
                      type={bairro.type}
                      label={bairro.label}
                      name={bairro.name}
                      value={values.bairro}
                      onChange={handleChange}
                      error={errors.bairro && touched.bairro}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormField
                      disabled={isCepFound}
                      type={cidade.type}
                      label={cidade.label}
                      name={cidade.name}
                      value={values.cidade}
                      onChange={handleChange}
                      error={errors.cidade && touched.cidade}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FormField
                      disabled={isCepFound}
                      type={uf.type}
                      label={uf.label}
                      name={uf.name}
                      value={values.uf}
                      onChange={handleChange}
                      error={errors.uf && touched.uf}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FormField
                      type={complemento.type}
                      label={complemento.label}
                      name={complemento.name}
                      value={values.complemento}
                      onChange={handleChange}
                      error={errors.complemento && touched.complemento}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>

                {!data && (
                  <>
                    <SectionHeader icon={<LockOutlinedIcon fontSize="small" />} title="Senha de acesso" />
                    <MDTypography variant="caption" color="text.secondary" display="block" mb={2}>
                      Crie uma senha para o organizador acessar o sistema e acompanhar seus eventos.
                    </MDTypography>
                    <Grid container spacing={2} mb={1}>
                      <Grid item xs={12} sm={6}>
                        <FormField
                          type={password.type}
                          label={password.label}
                          name={password.name}
                          value={values.password}
                          onChange={handleChange}
                          error={errors.password && touched.password}
                          sx={fieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormField
                          type={confirmPassword.type}
                          label={confirmPassword.label}
                          name={confirmPassword.name}
                          value={values.confirmPassword}
                          onChange={handleChange}
                          error={errors.confirmPassword && touched.confirmPassword}
                          sx={fieldSx}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </MDBox>

              <MDBox
                px={3}
                py={2}
                display="flex"
                gap={1}
                justifyContent="flex-end"
                borderTop="1px solid"
                borderColor="grey.200"
                sx={{ position: "sticky", bottom: 0, bgcolor: "background.paper", zIndex: 1 }}
              >
                <MDButton
                  type="button"
                  variant="outlined"
                  color="dark"
                  onClick={handlerClose}
                  disabled={isSubmitting}
                >
                  Cancelar
                </MDButton>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  disabled={isSubmitting}
                  sx={saveButtonSx}
                >
                  {isSubmitting ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar organizador"}
                </MDButton>
              </MDBox>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
